import {
  ContentService,
  type FlashcardDeckPublic,
  FlashcardDecksService,
  FlashcardsService,
  UsersService,
} from "@/client"
import { AppEmptyState } from "@/components/Common/AppEmptyState"
import { emptyStateCopy } from "@/components/Common/emptyStateCopy"
import { CustomStudyPanel } from "@/components/Flashcards/CustomStudyPanel"
import { StudyHelpButton } from "@/components/Flashcards/StudyHelpButton"
import { PageShell } from "@/components/Layout"
import { useColorModeValue } from "@/components/ui/color-mode"
import useAuth from "@/hooks/useAuth"
import { useFlashcardLeaderboardEnabled } from "@/hooks/useFlashcardLeaderboardEnabled"
import { useStudentLabels } from "@/hooks/useStudentLabels"
import {
  type StudySessionSnapshot,
  clearStudySession,
  loadStudySession,
} from "@/hooks/useStudySessionStore"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Heading,
  Icon,
  SimpleGrid,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import type { IconType } from "react-icons"
import {
  FaBolt,
  FaBookOpen,
  FaChevronDown,
  FaChevronRight,
  FaFire,
  FaLayerGroup,
  FaTags,
  FaTrophy,
} from "react-icons/fa"

export const Route = createFileRoute("/_layout/study/")({
  component: StudyHubPage,
})

export const DECK_GRID_COLUMNS = { base: 1, sm: 2, lg: 3, xl: 4 } as const

const SESSION_SIZES = [25, 50, 100] as const

function StudyHubPage() {
  const { flashcardsLabel } = useStudentLabels()
  const breadcrumbs = useRouteBreadcrumbs("study.index")
  const navigate = useNavigate()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [activeSession, setActiveSession] =
    useState<StudySessionSnapshot | null>(() => loadStudySession())
  const sessionSizeMutation = useMutation({
    mutationFn: (size: number) =>
      UsersService.updateUserMe({ requestBody: { study_session_size: size } }),
    onSettled: () => queryClient.invalidateQueries(),
  })
  const cardBg = useColorModeValue("white", "gray.900")
  const subtleCardBg = useColorModeValue("gray.50", "gray.800")
  const borderColor = useColorModeValue("gray.200", "gray.700")
  const mutedText = useColorModeValue("gray.600", "gray.300")
  const strongText = useColorModeValue("gray.900", "white")
  const heroBorder = useColorModeValue("teal.200", "teal.700")
  const progressTrack = useColorModeValue("gray.200", "gray.700")
  const leaderboardCurrentBg = useColorModeValue("teal.50", "teal.950")
  const leaderboardCurrentBorder = useColorModeValue("teal.200", "teal.700")
  const heroShadow = useColorModeValue(
    "0 24px 70px rgba(15, 118, 110, 0.14)",
    "0 24px 70px rgba(0, 0, 0, 0.36)",
  )
  const accentTopBg = useColorModeValue("teal.50", "teal.900")
  const accentBottomBg = useColorModeValue("yellow.50", "yellow.950")
  const deckGroupBg = useColorModeValue("white", "gray.900")
  const deckGroupHeaderBg = useColorModeValue("gray.50", "gray.800")
  const deckCardHoverBorder = useColorModeValue("gray.300", "gray.600")
  const [activeTagsByGroup, setActiveTagsByGroup] = useState<
    Record<string, string | undefined>
  >({})
  const [expandedDeckGroup, setExpandedDeckGroup] = useState<string | null>(
    null,
  )
  const [isMobileHeroLayout] = useState(() => {
    if (typeof window === "undefined") return false
    return window.matchMedia("(max-width: 767px)").matches
  })

  const { data: xpData } = useQuery({
    queryKey: ["my-xp"],
    queryFn: () => FlashcardsService.getMyXp(),
  })

  const { data: studyStats } = useQuery({
    queryKey: ["my-study-stats"],
    queryFn: () => FlashcardsService.getMyStudyStats(),
  })

  const { data: dueSummary } = useQuery({
    queryKey: ["flashcards-due-summary"],
    queryFn: () => FlashcardsService.getDueCardsSummary(),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  const { data: decksData, isFetching: isDecksFetching } = useQuery({
    queryKey: ["flashcard-decks", expandedDeckGroup],
    queryFn: () =>
      expandedDeckGroup === "miscellaneous"
        ? FlashcardDecksService.listDecks({ unassigned: true })
        : FlashcardDecksService.listDecks({ subjectId: expandedDeckGroup! }),
    enabled: expandedDeckGroup !== null,
  })

  const { data: deckSubjectsData } = useQuery({
    queryKey: ["flashcard-deck-subjects"],
    queryFn: () => FlashcardDecksService.listDeckSubjects(),
  })

  const { data: subjectsData } = useQuery({
    queryKey: ["subjects"],
    queryFn: () => ContentService.listSubjects({ limit: 100 }),
  })

  const { enabled: leaderboardEnabled } = useFlashcardLeaderboardEnabled()

  const { data: leaderboard } = useQuery({
    queryKey: ["flashcard-leaderboard"],
    queryFn: () => FlashcardsService.getLeaderboard(),
    enabled: leaderboardEnabled,
  })

  const totalXp = xpData?.total_xp ?? 0
  const cardsStudied = studyStats?.cards_studied ?? 0
  const decksReviewed = studyStats?.decks_reviewed ?? 0
  const level = xpData?.level ?? 1
  const levelName = xpData?.level_name ?? "Medical Student"
  const xpToNext = xpData?.xp_to_next_level
  const dueCount = dueSummary?.count ?? 0
  const subjectNameById = new Map(
    (subjectsData?.data ?? []).map((subject) => [subject.id, subject.name]),
  )
  const deckLibraryGroups = [
    ...(subjectsData?.data ?? [])
      .filter((subject) => deckSubjectsData?.subject_ids.includes(subject.id))
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((subject) => ({ key: subject.id, title: subject.name })),
    ...(deckSubjectsData?.has_unassigned
      ? [{ key: "miscellaneous", title: "Miscellaneous" }]
      : []),
  ]
  const deckGroups = useMemo(
    () => buildDeckGroups(decksData?.data ?? [], subjectNameById),
    [decksData?.data, subjectNameById],
  )
  const xpTotal = xpToNext ? totalXp + xpToNext : totalXp
  const progressPct = xpTotal > 0 ? Math.round((totalXp / xpTotal) * 100) : 100

  function resumeTo(snap: StudySessionSnapshot) {
    if (snap.mode === "deck") {
      navigate({
        to: "/study/$deckId",
        params: { deckId: snap.params.deckId! },
      })
    } else if (snap.mode === "custom") {
      navigate({
        to: "/study/custom",
        search: {
          subjectIds: snap.params.subjectIds,
          ratings: snap.params.ratings,
          includeNew: snap.params.includeNew,
          shuffle: snap.params.shuffle,
        },
      })
    } else {
      navigate({
        to: "/study/due",
        search: { subjectId: snap.params.subjectId },
      })
    }
  }

  return (
    <PageShell
      title={flashcardsLabel}
      description="Review due flashcards and track your progress."
      breadcrumbs={breadcrumbs}
    >
      {activeSession && (
        <ResumeSessionCard
          snapshot={activeSession}
          onResume={() => resumeTo(activeSession)}
          onDiscard={() => {
            clearStudySession()
            setActiveSession(null)
          }}
        />
      )}

      <Box
        position="relative"
        overflow="hidden"
        bg={cardBg}
        borderRadius="2xl"
        p={{ base: 5, md: 7 }}
        mb={2}
        borderWidth={1}
        borderColor={heroBorder}
        boxShadow={heroShadow}
      >
        <Box
          position="absolute"
          top="-74px"
          right="-60px"
          boxSize="180px"
          borderRadius="full"
          bg={accentTopBg}
        />
        <Box
          position="absolute"
          bottom="-96px"
          left="-84px"
          boxSize="190px"
          borderRadius="full"
          bg={accentBottomBg}
        />

        <Box position="relative">
          <Stack
            direction={isMobileHeroLayout ? "column" : "row"}
            justify="space-between"
            align={isMobileHeroLayout ? "stretch" : "flex-start"}
            gap={5}
            mb={6}
          >
            <HStack gap={4} align="center" flex="1" minW={0}>
              <Box
                bg="teal.500"
                borderRadius="full"
                boxSize={{ base: "58px", md: "68px" }}
                display="grid"
                placeItems="center"
                color="white"
                boxShadow="0 14px 30px rgba(13, 148, 136, 0.28)"
                flexShrink={0}
              >
                <Icon as={FaTrophy} boxSize={{ base: 6, md: 7 }} />
              </Box>
              <Box minW={0} maxW={{ md: "420px", lg: "520px" }}>
                <Text
                  fontSize="xs"
                  textTransform="uppercase"
                  letterSpacing="wider"
                  color="teal.600"
                  _dark={{ color: "teal.300" }}
                  fontWeight="bold"
                  mb={1}
                >
                  Keep the streak alive
                </Text>
                <Heading size={{ base: "md", md: "lg" }} color={strongText}>
                  Level {level} · {levelName}
                </Heading>
                <Text color={mutedText} fontSize="sm">
                  {totalXp} XP banked from flashcard reviews
                </Text>
              </Box>
            </HStack>

            <HStack
              aria-label="Due card summary"
              gap={3}
              align="center"
              justify={isMobileHeroLayout ? "space-between" : "flex-end"}
              w={isMobileHeroLayout ? "full" : "auto"}
              flexShrink={0}
              wrap="wrap"
            >
              <StudyHelpButton />
              <Box textAlign="right">
                <Text fontSize="3xl" fontWeight="bold" color="yellow.500">
                  {dueCount}
                </Text>
                <Text fontSize="xs" color={mutedText} fontWeight="semibold">
                  cards due
                </Text>
              </Box>
            </HStack>
          </Stack>

          <Box mb={6}>
            <HStack justify="space-between" mb={2}>
              <Text fontSize="sm" color={strongText} fontWeight="semibold">
                Level progress
              </Text>
              <Text fontSize="sm" color={mutedText}>
                {progressPct}%
              </Text>
            </HStack>
            <Box bg={progressTrack} borderRadius="full" h="10px">
              <Box
                bg="teal.500"
                borderRadius="full"
                h="100%"
                w={`${progressPct}%`}
                transition="width 0.3s"
              />
            </Box>
            {xpToNext && (
              <Text fontSize="xs" color={mutedText} mt={2}>
                {xpToNext} XP to next level
              </Text>
            )}
          </Box>

          <SimpleGrid columns={{ base: 1, md: 3 }} gap={3}>
            <StudyMetric
              icon={FaBolt}
              label="Total XP"
              value={String(totalXp)}
              color="yellow.500"
              bg={subtleCardBg}
              borderColor={borderColor}
            />
            <StudyMetric
              icon={FaFire}
              label="Cards studied"
              value={String(cardsStudied)}
              color="orange.500"
              bg={subtleCardBg}
              borderColor={borderColor}
            />
            <StudyMetric
              icon={FaLayerGroup}
              label="Decks reviewed"
              value={String(decksReviewed)}
              color="teal.500"
              bg={subtleCardBg}
              borderColor={borderColor}
            />
          </SimpleGrid>
        </Box>
      </Box>

      <HStack justify="space-between" align="center" mb={dueCount > 0 ? 2 : 6}>
        <Text fontSize="sm" color={mutedText} fontWeight="semibold">
          Cards per session
        </Text>
        <HStack gap={2}>
          {SESSION_SIZES.map((size) => {
            const isActive = (user?.study_session_size ?? 50) === size
            return (
              <Button
                key={size}
                size="sm"
                colorPalette="teal"
                variant={isActive ? "solid" : "outline"}
                onClick={() => sessionSizeMutation.mutate(size)}
                disabled={sessionSizeMutation.isPending}
              >
                {size}
              </Button>
            )
          })}
        </HStack>
      </HStack>

      {dueCount > 0 && (
        <Button
          width="full"
          mb={6}
          colorPalette="teal"
          onClick={() => {
            if (
              activeSession &&
              !window.confirm(
                "You have a session in progress. Start a new one?",
              )
            ) {
              return
            }
            clearStudySession()
            setActiveSession(null)
            navigate({ to: "/study/due" })
          }}
        >
          Study Due Cards
        </Button>
      )}

      <SectionHeading
        icon={FaLayerGroup}
        title="Custom study"
        caption="Drill cards by how well you know them."
      />
      <CustomStudyPanel />

      <SectionHeading
        icon={FaBookOpen}
        title="Deck library"
        caption="Pick a deck to review or re-study."
      />
      <VStack gap={5} align="stretch" mb={6}>
        {deckLibraryGroups.map((groupDefinition) => {
          const isOpen = expandedDeckGroup === groupDefinition.key
          const group = deckGroups.find(
            (loadedGroup) => loadedGroup.key === groupDefinition.key,
          ) ?? {
            key: groupDefinition.key,
            title: groupDefinition.title,
            decks: [],
            tags: [],
          }
          const activeTag = activeTagsByGroup[group.key]
          const visibleDecks = activeTag
            ? group.decks.filter((deck) => deck.tags?.includes(activeTag))
            : group.decks
          const groupDue = group.decks.reduce(
            (total, deck) => total + (deck.due_count ?? 0),
            0,
          )

          return (
            <Box
              key={group.key}
              borderWidth={1}
              borderRadius="lg"
              borderColor={borderColor}
              bg={deckGroupBg}
              overflow="hidden"
            >
              <Button
                variant="ghost"
                width="full"
                h="auto"
                px={{ base: 4, md: 5 }}
                py={4}
                bg={deckGroupHeaderBg}
                justifyContent="space-between"
                borderRadius={0}
                aria-expanded={isOpen}
                aria-label={`Deck group: ${group.title}`}
                _hover={{ bg: deckGroupHeaderBg }}
                onClick={() =>
                  setExpandedDeckGroup((current) =>
                    current === group.key ? null : group.key,
                  )
                }
              >
                <HStack gap={3}>
                  <Icon
                    as={isOpen ? FaChevronDown : FaChevronRight}
                    color={mutedText}
                    boxSize={3}
                  />
                  <Box textAlign="left">
                    <Text fontWeight="bold" color={strongText}>
                      {group.title}
                    </Text>
                  </Box>
                </HStack>
              </Button>

              {isOpen && (
                <Box
                  px={{ base: 4, md: 5 }}
                  pt={5}
                  pb={5}
                  borderTopWidth={1}
                  borderColor={borderColor}
                >
                  {isDecksFetching && group.decks.length === 0 && (
                    <Text fontSize="sm" color={mutedText} mb={4}>
                      Loading decks...
                    </Text>
                  )}

                  {groupDue > 0 && group.key !== "miscellaneous" && (
                    <Link
                      to="/study/due"
                      search={{ subjectId: group.key }}
                      style={{ display: "block", marginBottom: "1rem" }}
                    >
                      <Button width="full" size="sm" colorPalette="teal">
                        Study these ({groupDue} due)
                      </Button>
                    </Link>
                  )}

                  {group.tags.length > 0 && (
                    <Flex gap={2} wrap="wrap" mb={4} align="center">
                      <HStack color={mutedText} gap={1} mr={1}>
                        <Icon as={FaTags} boxSize={3} />
                        <Text fontSize="xs" fontWeight="semibold">
                          Tags
                        </Text>
                      </HStack>
                      {group.tags.map((tag) => (
                        <Button
                          key={tag}
                          size="xs"
                          borderRadius="full"
                          variant={activeTag === tag ? "solid" : "outline"}
                          colorPalette={activeTag === tag ? "teal" : "gray"}
                          onClick={() =>
                            setActiveTagsByGroup((current) => ({
                              ...current,
                              [group.key]:
                                current[group.key] === tag ? undefined : tag,
                            }))
                          }
                        >
                          {tag}
                        </Button>
                      ))}
                    </Flex>
                  )}

                  <SimpleGrid
                    gap={4}
                    justifyContent="start"
                    columns={DECK_GRID_COLUMNS}
                  >
                    {visibleDecks.map((deck) => (
                      <DeckLibraryCard
                        key={deck.id}
                        deck={deck}
                        borderColor={borderColor}
                        hoverBorderColor={deckCardHoverBorder}
                        bg={cardBg}
                        mutedText={mutedText}
                        strongText={strongText}
                        subjectName={
                          deck.subject_id
                            ? subjectNameById.get(deck.subject_id)
                            : undefined
                        }
                      />
                    ))}
                  </SimpleGrid>

                  {visibleDecks.length === 0 && (
                    <AppEmptyState
                      variant="inline"
                      title="No decks match this tag."
                      description="Clear the tag filter to see all decks in this group."
                    />
                  )}
                </Box>
              )}
            </Box>
          )
        })}
        {deckSubjectsData && deckLibraryGroups.length === 0 && (
          <AppEmptyState
            title={emptyStateCopy.flashcards.noDecks.title}
            description={emptyStateCopy.flashcards.noDecks.description}
            icon={<Icon as={FaBookOpen} boxSize={4} />}
          />
        )}
      </VStack>

      {leaderboardEnabled && (
        <>
          <SectionHeading
            icon={FaTrophy}
            title="Program Leaderboard"
            caption="Track your flashcard XP against your program."
          />
          <Box
            borderWidth={1}
            borderColor={borderColor}
            borderRadius="lg"
            overflow="hidden"
            bg={cardBg}
          >
            {leaderboard?.data.length === 0 && (
              <Text p={4} fontSize="sm" color={mutedText}>
                Not enrolled in a program yet.
              </Text>
            )}
            {leaderboard?.data.slice(0, 10).map((entry) => (
              <HStack
                key={String(entry.user_id)}
                px={4}
                py={3}
                justify="space-between"
                bg={entry.is_current_user ? leaderboardCurrentBg : cardBg}
                borderBottomWidth={1}
                borderColor={
                  entry.is_current_user ? leaderboardCurrentBorder : borderColor
                }
              >
                <HStack>
                  <Text
                    fontWeight="bold"
                    color={entry.rank === 1 ? "yellow.500" : mutedText}
                    minW="2.5rem"
                    flexShrink={0}
                    whiteSpace="nowrap"
                  >
                    #{entry.rank}
                  </Text>
                  <Text
                    fontSize="sm"
                    color={strongText}
                    fontWeight={entry.is_current_user ? "semibold" : undefined}
                  >
                    {entry.is_current_user ? "You" : entry.display_name}
                  </Text>
                </HStack>
                <Text
                  fontSize="sm"
                  color={entry.is_current_user ? "teal.500" : mutedText}
                  fontWeight={entry.is_current_user ? "semibold" : undefined}
                >
                  {entry.total_xp} XP
                </Text>
              </HStack>
            ))}
          </Box>
        </>
      )}
    </PageShell>
  )
}

type DeckGroup = {
  key: string
  title: string
  decks: FlashcardDeckPublic[]
  tags: string[]
}

function buildDeckGroups(
  decks: FlashcardDeckPublic[],
  subjectNameById: Map<string, string>,
): DeckGroup[] {
  const groupsByKey = new Map<string, DeckGroup>()

  for (const deck of decks) {
    const hasSubject = Boolean(deck.subject_id)
    const key = hasSubject ? String(deck.subject_id) : "miscellaneous"
    const title = hasSubject
      ? subjectNameById.get(String(deck.subject_id)) ?? "Untitled subject"
      : "Miscellaneous"

    if (!groupsByKey.has(key)) {
      groupsByKey.set(key, { key, title, decks: [], tags: [] })
    }

    groupsByKey.get(key)?.decks.push(deck)
  }

  const groups = Array.from(groupsByKey.values()).map((group) => ({
    ...group,
    decks: [...group.decks].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    ),
    tags: Array.from(
      new Set(group.decks.flatMap((deck) => deck.tags ?? [])),
    ).sort((a, b) => a.localeCompare(b)),
  }))

  return groups.sort((a, b) =>
    a.title.localeCompare(b.title, undefined, { sensitivity: "base" }),
  )
}

function ResumeSessionCard({
  snapshot,
  onResume,
  onDiscard,
}: {
  snapshot: StudySessionSnapshot
  onResume: () => void
  onDiscard: () => void
}) {
  const cardBg = useColorModeValue("teal.50", "teal.950")
  const borderColor = useColorModeValue("teal.200", "teal.700")
  const strongText = useColorModeValue("gray.900", "white")
  const mutedText = useColorModeValue("gray.600", "gray.300")
  const currentCard = snapshot.index + 1
  const totalCards = snapshot.cards.length

  return (
    <Box
      bg={cardBg}
      borderWidth={1}
      borderColor={borderColor}
      borderRadius="lg"
      px={{ base: 4, md: 5 }}
      py={4}
      mb={4}
    >
      <HStack justify="space-between" align="center" gap={4} flexWrap="wrap">
        <Box>
          <Text fontWeight="bold" color={strongText}>
            Resume session
          </Text>
          <Text fontSize="sm" color={mutedText}>
            card {currentCard} of {totalCards}
          </Text>
        </Box>
        <HStack gap={2}>
          <Button size="sm" colorPalette="teal" onClick={onResume}>
            Resume
          </Button>
          <Button
            size="sm"
            variant="outline"
            colorPalette="gray"
            onClick={onDiscard}
          >
            Discard
          </Button>
        </HStack>
      </HStack>
    </Box>
  )
}

function DeckLibraryCard({
  deck,
  subjectName,
  borderColor,
  hoverBorderColor,
  bg,
  mutedText,
  strongText,
}: {
  deck: FlashcardDeckPublic
  subjectName?: string
  borderColor: string
  hoverBorderColor: string
  bg: string
  mutedText: string
  strongText: string
}) {
  return (
    <Box
      p={4}
      borderWidth={1}
      borderRadius="lg"
      borderColor={borderColor}
      bg={bg}
      boxShadow="sm"
      minH="216px"
      transition="border-color 0.16s ease, box-shadow 0.16s ease"
      _hover={{ borderColor: hoverBorderColor, boxShadow: "md" }}
    >
      <VStack align="stretch" gap={3} h="100%">
        <Box flex="1">
          <HStack justify="space-between" align="start" gap={3}>
            <Box minW={0}>
              <Text fontWeight="bold" color={strongText} lineClamp={2}>
                {deck.name}
              </Text>
              <Text fontSize="xs" color={mutedText} mt={1}>
                {deck.card_count ?? 0} cards ·{" "}
                {(deck.programs?.length ?? 0) > 0 ? "Program" : "All students"}
              </Text>
            </Box>
            <Badge colorPalette="yellow" variant="subtle" flexShrink={0}>
              {deck.due_count ?? 0} due
            </Badge>
          </HStack>

          <Flex gap={2} wrap="wrap" mt={3}>
            {subjectName && <Badge colorPalette="teal">{subjectName}</Badge>}
            {(deck.tags ?? []).map((tag) => (
              <Badge key={tag} colorPalette="blue" variant="subtle">
                {tag}
              </Badge>
            ))}
          </Flex>
        </Box>

        <Link to={`/study/${deck.id}`}>
          <Button
            size="sm"
            width="full"
            variant="outline"
            colorPalette="teal"
            _hover={{ bg, borderColor: hoverBorderColor }}
          >
            Study
          </Button>
        </Link>
      </VStack>
    </Box>
  )
}

function SectionHeading({
  icon,
  title,
  caption,
}: {
  icon: IconType
  title: string
  caption: string
}) {
  return (
    <HStack gap={3} align="center" mb={3}>
      <Box
        boxSize="34px"
        borderRadius="full"
        bg="teal.50"
        color="teal.500"
        display="grid"
        placeItems="center"
        flexShrink={0}
        _dark={{ bg: "teal.950", color: "teal.300" }}
      >
        <Icon as={icon} boxSize={4} />
      </Box>
      <Box>
        <Text
          fontSize="xs"
          textTransform="uppercase"
          letterSpacing="wider"
          color="gray.600"
          _dark={{ color: "gray.300" }}
          fontWeight="bold"
        >
          {title}
        </Text>
        <Text fontSize="sm" color="gray.600" _dark={{ color: "gray.400" }}>
          {caption}
        </Text>
      </Box>
    </HStack>
  )
}

function StudyMetric({
  icon,
  label,
  value,
  color,
  bg,
  borderColor,
}: {
  icon: IconType
  label: string
  value: string
  color: string
  bg: string
  borderColor: string
}) {
  return (
    <Box
      borderWidth={1}
      borderColor={borderColor}
      borderRadius="lg"
      bg={bg}
      p={4}
    >
      <HStack gap={3}>
        <Box
          boxSize="36px"
          borderRadius="full"
          bg="white"
          color={color}
          display="grid"
          placeItems="center"
          flexShrink={0}
          _dark={{ bg: "gray.950" }}
        >
          <Icon as={icon} />
        </Box>
        <Box>
          <Text fontSize="xl" fontWeight="bold" lineHeight={1}>
            {value}
          </Text>
          <Text fontSize="xs" color="gray.600" _dark={{ color: "gray.400" }}>
            {label}
          </Text>
        </Box>
      </HStack>
    </Box>
  )
}

export default StudyHubPage
